import { useState } from "react";
import { startOfToday, format } from "date-fns";

import useData from "./useData";
import useOrder from "./useOrder";
import useSKU from "./useSKU";
import useLocation from "./useLocation";

const useSurvey = (providedSurvey) => {
  const { executeAction, getFromServer, isDateBetween, findOne, hasValue } =
    useData();
  const { getOrderByCallResult } = useOrder();
  const { skuListings, skuPricing } = useSKU();
  const { getLocationById } = useLocation();

  const [survey, setSurvey] = useState(providedSurvey || null);
  const [surveys, setSurveys] = useState([]);
  const [order, setOrder] = useState(null);
  const [callResults, setCallResults] = useState(null);
  const [surveyLoaded, setSurveyLoaded] = useState(false);
  const [skuList, setSkuList] = useState([]);

  const questionStatus = {
    required_missing: {
      required: true,
      answered: false,
      colorCode: "#ff0000",
    },
    required_answered: {
      required: true,
      answered: true,
      colorCode: "#00ff00",
    },
    optional_missing: {
      required: false,
      answered: false,
      colorCode: "#ffff00",
    },
    optional_answered: {
      required: false,
      answered: true,
      colorCode: "#00ff00",
    },
  };

  const skuStatus = {
    required_missing: {
      required: true,
      answered: false,
      colorCode: "#ff0000",
    },
    required_answered: {
      required: true,
      answered: true,
      colorCode: "#00ff00",
    },
    optional_missing: {
      required: false,
      answered: false,
      colorCode: "#ffff00",
    },
    optional_answered: {
      required: false,
      answered: true,
      colorCode: "#00ff00",
    },
  };

  const sectionStatus = {
    missing: {
      required: true,
      answered: false,
      colorCode: "#ff0000",
    },
    optional: {
      required: false,
      answered: false,
      colorCode: "#cccccc",
    },
    answered: {
      required: true,
      answered: true,
      colorCode: "#00ff00",
    },
  };

  const pageStatus = {
    missing: {
      required: true,
      answered: false,
      colorCode: "#ff0000",
    },
    optional: {
      required: false,
      answered: false,
      colorCode: "#cccccc",
    },
    answered: {
      required: true,
      answered: true,
      colorCode: "#00ff00",
    },
  };

  const getById = async (surveyId) => {
    const results = await getFromServer({
      entityName: "Survey",
      pipeline: "full",
      id: surveyId,
    });

    if (results) {
      setSurvey(results);
      return results;
    } else {
      setSurvey(null);
      return null;
    }
  };

  const getSurvey = async (surveyId) => {
    const results = await getFromServer({
      entityName: "Survey",
      pipeline: "withClient",
      id: surveyId,
    });

    return results;
  };

  const getSurveysForClient = async (clientId) => {
    const results = await executeAction({
      entityName: "Survey",
      action: "getSurveysForClient",
      data: { clientId },
    });

    if (results?.isSuccessful && Array.isArray(results.data)) {
      setSurveys(results.data);
    } else {
      setSurveys([]);
    }
  };

  const getAllSurveys = async () => {
    const results = await executeAction({
      entityName: "Survey",
      action: "getSurveysForClient",
      data: { clientId: null },
    });

    console.log("results: ", results);

    if (results?.isSuccessful && Array.isArray(results.data)) {
      setSurveys(results.data);
    } else {
      setSurveys([]);
    }
  };

  const getSurveysForProject = async (projectId) => {
    const results = await executeAction({
      entityName: "Survey",
      action: "getSurveysForProject",
      data: { projectId },
    });

    return results?.isSuccessful && Array.isArray(results.data)
      ? results.data
      : [];
  };

  const getQuestionStatus = async (question, answer) => {
    const isImageAnswered = (value) => {
      const hasImagePayload = (image) => {
        if (!image) return false;
        if (typeof image === "string") return image.trim() !== "";
        if (typeof image !== "object") return false;
        return Boolean(image.data || image.fileName || image.url);
      };

      if (!value) return false;

      // If already an array (legacy filenames) => any entry means answered
      if (Array.isArray(value)) return value.length > 0;

      // If serialized JSON
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return isImageAnswered(parsed);
        } catch {
          return false;
        }
      }

      // Structured object (before/after or standard)
      if (value && typeof value === "object") {
        if (Array.isArray(value.before) || Array.isArray(value.after)) {
          const before = Array.isArray(value.before) ? value.before : [];
          const after = Array.isArray(value.after) ? value.after : [];
          return (
            before.some((image) => hasImagePayload(image)) &&
            after.some((image) => hasImagePayload(image))
          );
        }

        // Newer shape: { images: [ { before:{data}, after:{data}, standard:{data} } ] }
        if (Array.isArray(value.images)) {
          for (const img of value.images) {
            const hasStandard = hasImagePayload(img?.standard || img);
            const hasBeforeAfter =
              hasImagePayload(img?.before) && hasImagePayload(img?.after);
            if (hasStandard || hasBeforeAfter) return true;
          }
          return false;
        }

        // Older shape: maybe a map of before/after
        if (hasImagePayload(value.before) && hasImagePayload(value.after))
          return true;
        if (hasImagePayload(value.standard || value)) return true;
      }

      return false;
    };

    const hasAnswer = (() => {
      if (!answer) return false;
      const val = answer.value;

      // Handle images explicitly
      if (question.questionType === "image") {
        return isImageAnswered(val);
      }

      if (question.questionType === "distribution") {
        return isDistributionAnswered(val);
      }

      if (question.questionType === "signature") {
        if (!val) return false;

        let signatureValue = val;
        if (typeof signatureValue === "string") {
          try {
            signatureValue = JSON.parse(signatureValue);
          } catch {
            return false;
          }
        }

        if (!signatureValue || typeof signatureValue !== "object") {
          return false;
        }

        return (
          typeof signatureValue.signature === "string" &&
          signatureValue.signature.trim() !== ""
        );
      }

      // Arrays must be non-empty
      if (Array.isArray(val)) return val.length > 0;

      // Scalars / objects: truthy value counts
      return val !== null && val !== undefined && val !== "";
    })();

    const required = Number(question?.isRequired ?? 0) === 1;

    if (required && !hasAnswer) return questionStatus.required_missing;
    if (required && hasAnswer) return questionStatus.required_answered;
    if (!required && !hasAnswer) return questionStatus.optional_missing;
    if (!required && hasAnswer) return questionStatus.optional_answered;
  };

  const showSurvey = async (args) => {
    let show = true;

    show = await checkSurveyAvailability(args);
    if (show) show = await checkSurveyVisibility(args);

    return show;
  };

  const checkSurveyAvailability = async (args) => {
    return isInAvailabilityRange(args.survey?.availability);
  };

  const checkSurveyVisibility = async (args) => {
    return isVisibleForLocation(args.survey?.visibility, args.location);
  };

  const showPage = async (args) => {
    if (!isActiveFlag(args?.page?.isActive)) return false;

    let show = true;

    show = await checkPageLogic(args);
    if (show) show = await checkPageAvailability(args);
    if (show) show = await checkPageVisibility(args);

    return show;
  };

  const normalizeControlValue = (value) => {
    if (typeof value !== "string") return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const normalizeControlledOptions = (value) => {
    const parsed = normalizeControlValue(value);
    const normalized = Array.isArray(parsed) ? parsed : [parsed];

    return normalized.filter(
      (item) => item !== null && item !== undefined && item !== "",
    );
  };

  const normalizeControlledBy = (controlledBy) => {
    const parsed = normalizeControlValue(controlledBy);
    if (!parsed || typeof parsed !== "object") return null;

    const questionId = parsed.questionId;
    if (!questionId) return null;

    return parsed;
  };

  const isControllingQuestionMissingOrInactive = (questionId, args) => {
    const candidates = [
      ...(Array.isArray(args?.questions) ? args.questions : []),
      ...(Array.isArray(survey?.questions) ? survey.questions : []),
    ];

    if (candidates.length === 0) {
      return true;
    }

    const targetId = String(questionId);
    const controllingQuestion = candidates.find(
      (question) => String(question?.id) === targetId,
    );

    if (!controllingQuestion) {
      return true;
    }

    const controllerIsActive = Number(controllingQuestion?.isActive ?? 1) === 1;
    return !controllerIsActive;
  };

  const hasAnsweredValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  };

  const sameId = (left, right) => String(left ?? "") === String(right ?? "");
  const isActiveFlag = (value) => Number(value ?? 1) === 1;
  const isRequiredFlag = (value) => Number(value ?? 0) === 1;

  const normalizeAvailabilityRules = (availability) => {
    if (
      availability === null ||
      availability === undefined ||
      availability === ""
    ) {
      return [];
    }

    let parsed = availability;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    return [];
  };

  const isInAvailabilityRange = (availability) => {
    const rules = normalizeAvailabilityRules(availability);

    if (rules.length === 0) {
      return true;
    }

    const today = format(startOfToday(), "yyyy-MM-dd");
    for (const dateRange of rules) {
      if (isDateBetween(today, dateRange.startsOn, dateRange.endsOn)) {
        return true;
      }
    }

    return false;
  };

  const normalizeVisibilityRules = (visibility) => {
    if (visibility === null || visibility === undefined || visibility === "") {
      return [];
    }

    let parsed = visibility;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return [{ __invalid: true }];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (typeof parsed === "object") {
      return [parsed];
    }

    return [];
  };

  const toComparable = (value) =>
    value === null || value === undefined ? "" : String(value).trim();

  const isVisibleForLocation = (visibility, location) => {
    const rules = normalizeVisibilityRules(visibility);

    if (rules.length === 0) {
      return true;
    }

    if (!location) {
      return false;
    }

    if (rules.some((rule) => rule?.__invalid)) {
      return false;
    }

    const retailerId = toComparable(
      location.retailerId ||
        location.banner?.retailerId ||
        location.retailer?.id,
    );
    const bannerId = toComparable(location.bannerId || location.banner?.id);
    const province = toComparable(
      location.province || location.address?.province,
    ).toUpperCase();

    for (const option of rules) {
      let isVisible = false;

      const optionName = toComparable(option?.name).toLowerCase();
      const optionValue = toComparable(option?.value);

      if (optionName === "retailer") {
        isVisible = retailerId === optionValue;
      }
      if (optionName === "banner") {
        isVisible = bannerId === optionValue;
      }
      if (optionName === "province") {
        isVisible = province === optionValue.toUpperCase();
      }

      if (isVisible) {
        return true;
      }
    }

    return false;
  };

  const checkPageLogic = async (args) => {
    const controlledBy = normalizeControlledBy(args.page?.controlledBy);

    if (!controlledBy) {
      return true;
    }

    if (isControllingQuestionMissingOrInactive(controlledBy.questionId, args)) {
      return true;
    }

    let filter = { surveyQuestionId: controlledBy.questionId };
    if (args.skuId) {
      filter.skuId = args.skuId;
    }

    const answer = findOne(args.answers, filter);

    if (!answer) return false;
    if (!hasAnsweredValue(answer.value)) return false;

    const controlledOptions = normalizeControlledOptions(controlledBy.value);
    if (controlledOptions.length === 0) return false;

    const answerValue = normalizeControlValue(answer.value);
    if (hasValue(controlledOptions, answerValue)) {
      return true;
    }

    return false;
  };

  const checkPageAvailability = async (args) => {
    return isInAvailabilityRange(args.page?.availability);
  };

  const checkPageVisibility = async (args) => {
    return isVisibleForLocation(args.page?.visibility, args.location);
  };

  const showSection = async (args) => {
    if (!isActiveFlag(args?.section?.isActive)) return false;

    let show = true;

    show = await checkSectionLogic(args);
    if (show) show = await checkSectionAvailability(args);
    if (show) show = await checkSectionVisibility(args);

    return show;
  };

  const checkSectionLogic = async (args) => {
    const controlledBy = normalizeControlledBy(args.section?.controlledBy);

    if (!controlledBy) {
      return true;
    }

    if (isControllingQuestionMissingOrInactive(controlledBy.questionId, args)) {
      return true;
    }

    let filter = { surveyQuestionId: controlledBy.questionId };
    if (args.skuId) {
      filter.skuId = args.skuId;
    }

    const answer = findOne(args.answers, filter);

    if (!answer) return false;
    if (!hasAnsweredValue(answer.value)) return false;

    const controlledOptions = normalizeControlledOptions(controlledBy.value);
    if (controlledOptions.length === 0) return false;

    const answerValue = normalizeControlValue(answer.value);
    if (hasValue(controlledOptions, answerValue)) {
      return true;
    }

    return false;
  };

  const checkSectionAvailability = async (args) => {
    return isInAvailabilityRange(args.section?.availability);
  };

  const checkSectionVisibility = async (args) => {
    return isVisibleForLocation(args.section?.visibility, args.location);
  };

  const showQuestion = async (args) => {
    if (!isActiveFlag(args?.question?.isActive)) return false;

    let show = true;

    show = await checkQuestionLogic(args);
    if (show) show = await checkQuestionAvailability(args);
    if (show) show = await checkQuestionVisibility(args);

    return show;
  };

  const checkQuestionLogic = async (args) => {
    const controlledBy = normalizeControlledBy(args.question?.controlledBy);

    if (!controlledBy) {
      return true;
    }

    if (isControllingQuestionMissingOrInactive(controlledBy.questionId, args)) {
      return true;
    }

    let filter = { surveyQuestionId: controlledBy.questionId };
    if (args.skuId) {
      filter.skuId = args.skuId;
    }

    const answer = findOne(args.answers, filter);

    if (!answer) return false;
    if (!hasAnsweredValue(answer.value)) return false;

    const controlledOptions = normalizeControlledOptions(controlledBy.value);
    if (controlledOptions.length === 0) return false;

    const answerValue = normalizeControlValue(answer.value);
    if (hasValue(controlledOptions, answerValue)) {
      return true;
    }

    return false;
  };

  const checkQuestionAvailability = async (args) => {
    return isInAvailabilityRange(args.question?.availability);
  };

  const checkQuestionVisibility = async (args) => {
    return isVisibleForLocation(args.question?.visibility, args.location);
  };

  const isImageAnswered = (value) => {
    const hasImagePayload = (image) => {
      if (!image) return false;
      if (typeof image === "string") return image.trim() !== "";
      if (typeof image !== "object") return false;
      return Boolean(image.data || image.fileName || image.url);
    };

    if (!value) return false;

    if (Array.isArray(value)) return value.length > 0;

    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return isImageAnswered(parsed);
      } catch {
        return false;
      }
    }

    if (value && typeof value === "object") {
      if (Array.isArray(value.before) || Array.isArray(value.after)) {
        const before = Array.isArray(value.before) ? value.before : [];
        const after = Array.isArray(value.after) ? value.after : [];
        return (
          before.some((image) => hasImagePayload(image)) &&
          after.some((image) => hasImagePayload(image))
        );
      }

      if (Array.isArray(value.images)) {
        for (const img of value.images) {
          const hasStandard = hasImagePayload(img?.standard || img);
          const hasBeforeAfter =
            hasImagePayload(img?.before) && hasImagePayload(img?.after);
          if (hasStandard || hasBeforeAfter) return true;
        }
        return false;
      }

      if (hasImagePayload(value.before) && hasImagePayload(value.after))
        return true;
      if (hasImagePayload(value.standard || value)) return true;
    }

    return false;
  };

  const parseJsonSafe = (value) => {
    if (typeof value !== "string") return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const hasDistributionPhoto = (value) => {
    const parsed = parseJsonSafe(value);

    if (Array.isArray(parsed)) {
      return parsed.length > 0;
    }

    if (parsed && typeof parsed === "object") {
      return Boolean(parsed.fileName || parsed.url || parsed.data);
    }

    return false;
  };

  const normalizeAvailableForSale = (value) => {
    if (value === 1 || value === "1" || value === true || value === "true") {
      return 1;
    }

    if (value === 0 || value === "0" || value === false || value === "false") {
      return 0;
    }

    return null;
  };

  const isDistributionAnswered = (val) => {
    const parsed = parseJsonSafe(val);
    if (!parsed || typeof parsed !== "object") return false;

    const availableForSale = normalizeAvailableForSale(parsed.availableForSale);
    if (availableForSale === null) return false;

    // YES path (availableForSale === 1)
    if (availableForSale === 1) {
      // Must have entry photo
      return hasDistributionPhoto(parsed.entryPhoto);
    }

    // NO path (availableForSale === 0)
    if (availableForSale === 0) {
      // Must have action selected
      if (!parsed.action) return false;

      // Actions that require photos (1-4): placedTagandProduct, tag-NoBackroomAccess, tag-ProductOrdered, noTag-ProductOnShelf
      const photoActions = [
        "placedTagandProduct",
        "tag-NoBackroomAccess",
        "tag-ProductOrdered",
        "noTag-ProductOnShelf",
      ];

      // Actions that require follow-up (4, 5, 6): noTag-ProductOnShelf, noTag-NoProduct-ProductOrdered, planogramNotCompleted
      const followUpActions = [
        "noTag-ProductOnShelf",
        "noTag-NoProduct-ProductOrdered",
        "planogramNotCompleted",
      ];

      // If action needs photo, must have action photo
      if (photoActions.includes(parsed.action)) {
        if (!hasDistributionPhoto(parsed.actionPhoto)) {
          return false;
        }
      }

      // If action needs follow-up, must have follow-up answer
      if (followUpActions.includes(parsed.action)) {
        if (!parsed.followUp || !parsed.followUp.answer) {
          return false;
        }
      }

      return true;
    }

    return false;
  };

  const isAnswered = (question, val) => {
    if (question.questionType === "image") return isImageAnswered(val);
    if (question.questionType === "distribution")
      return isDistributionAnswered(val);
    if (question.questionType === "signature") {
      if (!val) return false;

      let signatureValue = val;
      if (typeof signatureValue === "string") {
        try {
          signatureValue = JSON.parse(signatureValue);
        } catch {
          return false;
        }
      }

      if (!signatureValue || typeof signatureValue !== "object") {
        return false;
      }

      return (
        typeof signatureValue.signature === "string" &&
        signatureValue.signature.trim() !== ""
      );
    }
    if (Array.isArray(val)) return val.length > 0;
    return val !== null && val !== undefined && val !== "";
  };

  const getSkuStatus = async (section, questions, sku, answers, location) => {
    const sectionQuestions = questions.filter((question) =>
      sameId(question.surveySectionId, section.id),
    );

    let numRequiredQuestions = 0;
    let numRequiredAnswered = 0;
    let numOptionalQuestions = 0;
    let numOptionalAnswered = 0;
    let hasOptionalMissing = false;

    for (const question of sectionQuestions) {
      const show = await showQuestion({
        question,
        questions,
        answers,
        location,
        skuId: sku?.id,
      });
      if (!show) continue;

      const answer = answers.find(
        (a) =>
          sameId(a.surveyQuestionId, question.id) && sameId(a.skuId, sku.id),
      );

      const answered = !!answer && isAnswered(question, answer.value);

      if (question.questionType === "distribution") {
        let distributionValue = answer?.value;
        if (typeof distributionValue === "string") {
          try {
            distributionValue = JSON.parse(distributionValue);
          } catch {
            distributionValue = null;
          }
        }

        const availabilitySet =
          distributionValue &&
          (distributionValue.availableForSale === true ||
            distributionValue.availableForSale === false ||
            distributionValue.availableForSale === "true" ||
            distributionValue.availableForSale === "false" ||
            distributionValue.availableForSale === 1 ||
            distributionValue.availableForSale === 0 ||
            distributionValue.availableForSale === "1" ||
            distributionValue.availableForSale === "0");

        if (!availabilitySet) {
          if (isRequiredFlag(question.isRequired))
            return skuStatus.required_missing;
          hasOptionalMissing = true;
          continue;
        }
      }

      if (isRequiredFlag(question.isRequired)) {
        numRequiredQuestions++;
        if (answered) numRequiredAnswered++;
      } else {
        numOptionalQuestions++;
        if (answered) numOptionalAnswered++;
        else hasOptionalMissing = true;
      }
    }

    if (numRequiredQuestions > 0) {
      if (numRequiredAnswered === numRequiredQuestions) {
        return skuStatus.required_answered;
      }
      return skuStatus.required_missing;
    }

    if (numOptionalQuestions > 0) {
      if (numOptionalAnswered === numOptionalQuestions && !hasOptionalMissing) {
        return skuStatus.optional_answered;
      }
      return skuStatus.optional_missing;
    }

    if (hasOptionalMissing) return skuStatus.optional_missing;
    return skuStatus.optional_missing;
  };

  const getSkuMatrixStatus = async (
    section,
    questions,
    answers,
    location,
    call = null,
  ) => {
    let hasRequiredAnswered = false;
    let hasOptionalMissing = false;
    let hasOptionalAnswered = false;

    const matrixItems = Array.isArray(section?.skuMatrix?.items)
      ? section.skuMatrix.items
      : [];

    let activeSkuIds = matrixItems
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return null;
        return item.skuId || item.id || null;
      })
      .filter(Boolean);

    const applyListingsEnabled =
      section?.skuMatrix?.applyListings === true ||
      section?.skuMatrix?.applyListings === "true" ||
      section?.skuMatrix?.applyListings === 1;

    if (applyListingsEnabled && call?.id) {
      const listedSkus = await skuListings(call.id);
      const listedSkuIds = Array.isArray(listedSkus)
        ? listedSkus
            .map((item) => item?.skuId || item?.id)
            .filter(Boolean)
            .map((id) => String(id))
        : [];

      activeSkuIds = activeSkuIds.filter((id) =>
        listedSkuIds.includes(String(id)),
      );
    }

    for (const skuId of activeSkuIds) {
      const skuStat = await getSkuStatus(
        section,
        questions,
        { id: skuId },
        answers,
        location,
      );

      if (skuStat.required && !skuStat.answered) {
        return skuStatus.required_missing;
      }

      if (skuStat.required && skuStat.answered) {
        hasRequiredAnswered = true;
      }

      if (!skuStat.required && skuStat.answered) {
        hasOptionalAnswered = true;
      }

      if (!skuStat.required && !skuStat.answered) {
        hasOptionalMissing = true;
      }
    }

    if (hasOptionalMissing) return skuStatus.optional_missing;
    if (hasRequiredAnswered) return skuStatus.required_answered;
    if (hasOptionalAnswered) return skuStatus.optional_answered;
    return skuStatus.optional_missing;
  };

  const calculateQuestionAggregate = async (
    section,
    questions,
    answers,
    location,
  ) => {
    const sectionQuestions = questions.filter((question) =>
      sameId(question.surveySectionId, section.id),
    );

    let requiredCount = 0;
    let requiredAnswered = 0;
    let optionalCount = 0;
    let optionalAnswered = 0;

    for (const question of sectionQuestions) {
      const show = await showQuestion({
        question,
        questions,
        answers,
        location,
      });
      if (!show) continue;

      const answer = answers.find((a) =>
        sameId(a.surveyQuestionId, question.id),
      );
      const answered = !!answer && isAnswered(question, answer.value);

      if (isRequiredFlag(question.isRequired)) {
        requiredCount++;
        if (answered) requiredAnswered++;
      } else {
        optionalCount++;
        if (answered) optionalAnswered++;
      }
    }

    return {
      requiredCount,
      requiredAnswered,
      optionalCount,
      optionalAnswered,
      allRequiredComplete:
        requiredCount === 0 || requiredAnswered === requiredCount,
    };
  };

  const calculatePageAggregate = async (
    page,
    sections,
    questions,
    answers,
    location,
    call = null,
  ) => {
    const pageSections = sections.filter((section) =>
      sameId(section.surveyPageId, page.id),
    );

    let requiredSectionCount = 0;
    let completedRequiredSectionCount = 0;

    for (const section of pageSections) {
      const show = await showSection({ section, answers, location });
      if (!show) continue;

      if (section.sectionType === "question") {
        const aggregate = await calculateQuestionAggregate(
          section,
          questions,
          answers,
          location,
        );

        if (aggregate.requiredCount > 0) {
          requiredSectionCount++;
          if (aggregate.allRequiredComplete) {
            completedRequiredSectionCount++;
          }
        }
      } else if (section.sectionType === "sku") {
        const skuSectionStatus = await getSkuMatrixStatus(
          section,
          questions,
          answers,
          location,
          call,
        );

        if (skuSectionStatus.required) {
          requiredSectionCount++;
          if (skuSectionStatus.answered) {
            completedRequiredSectionCount++;
          }
        }
      }
    }

    return {
      requiredSectionCount,
      completedRequiredSectionCount,
      allRequiredComplete:
        requiredSectionCount === 0 ||
        completedRequiredSectionCount === requiredSectionCount,
    };
  };

  const getQuestionSectionStatus = async (
    section,
    questions,
    answers,
    location,
  ) => {
    const aggregate = await calculateQuestionAggregate(
      section,
      questions,
      answers,
      location,
    );

    if (aggregate.requiredCount > 0) {
      if (aggregate.allRequiredComplete) {
        return sectionStatus.answered;
      } else {
        return sectionStatus.missing;
      }
    }

    if (aggregate.optionalCount > 0) {
      if (aggregate.optionalAnswered === aggregate.optionalCount) {
        return sectionStatus.answered;
      } else {
        return sectionStatus.optional;
      }
    }

    return sectionStatus.optional;
  };

  const getPageStatus = async (
    page,
    sections,
    questions,
    answers,
    location,
    call = null,
  ) => {
    const aggregate = await calculatePageAggregate(
      page,
      sections,
      questions,
      answers,
      location,
      call,
    );

    if (!aggregate.allRequiredComplete) {
      return pageStatus.missing;
    }
    if (aggregate.requiredSectionCount > 0) {
      return pageStatus.answered;
    }

    return pageStatus.optional;
  };

  const hasAnswer = (answer) => {
    if (!answer) answer = null;
    if (answer === null) {
      hasAnswer = false;
    } else if (Array.isArray(answer.value) && answer.value.length === 0) {
      hasAnswer = false;
    }

    return hasAnswer;
  };

  const getSurveyStatus = async ({ call, surveyId, answers }) => {
    let surveyStatus = {
      completed: true,
      missingAnswers: [],
      colorCode: "#00ff00",
    };

    const location = await getLocationById(call.locationId);

    const survey = await getFromServer({
      entityName: "Survey",
      pipeline: "full",
      id: surveyId,
    });

    if (survey?.surveyType === "order") {
      const toDateKey = (value) => {
        if (!value) return "";
        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return "";

        const year = parsed.getFullYear();
        const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
        const day = `${parsed.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const callResultRows = await getFromServer({
        entityName: "CallResult",
        filter: { callId: call.id, surveyId },
      });

      const callResult = Array.isArray(callResultRows)
        ? callResultRows[0]
        : null;
      const orderRows = callResult?.id
        ? await getOrderByCallResult(callResult.id)
        : [];

      const order = Array.isArray(orderRows) ? orderRows[0] : null;
      const sentAt = String(order?.sentAt || "").trim();

      let complete = false;
      if (sentAt) {
        const callDateKey = toDateKey(call?.yyyymmdd || call?.scheduleDate);
        if (callDateKey) {
          complete = toDateKey(sentAt) === callDateKey;
        } else {
          complete = true;
        }
      }

      return {
        ...surveyStatus,
        completed: complete,
        colorCode: complete ? "#00ff00" : "#ff0000",
      };
    }

    for (const page of survey.pages) {
      const show = await showPage({ page, answers, location });
      if (!show) continue;

      const pageStatus = await getPageStatus(
        page,
        survey.sections,
        survey.questions,
        answers,
        location,
        call,
      );

      if (pageStatus.required && !pageStatus.answered) {
        surveyStatus.completed = false;
        surveyStatus.colorCode = "#ff0000";
        break;
      }
    }

    return surveyStatus;
  };

  // const getSurveyStatus = (pages, sections, questions, answers) => {
  //   let surveyStatus = {
  //     completed: true,
  //     missingAnswers: [],
  //     colorCode: "#00ff00",
  //   };

  //   for (const page of pages) {
  //     if (!page.isActive) continue;

  //     const pageSections = sections.filter(
  //       (section) => section.surveyPageId === page.id && section.isActive
  //     );

  //     if (Array.isArray(pageSections)) {
  //       for (const section of pageSections) {
  //         if (section.sectionType === "text") continue;

  //         const sectionQuestions = questions.filter(
  //           (question) =>
  //             question.surveySectionId === section.id && question.isActive
  //         );

  //         if (Array.isArray(sectionQuestions)) {
  //           let answer = null;

  //           if (section.sectionType === "question") {
  //             for (const question of sectionQuestions) {
  //               answer = answers.find(
  //                 (answer) => answer.surveyQuestionId === question.id
  //               );

  //               if (question.isRequired && !hasAnswer(answer)) {
  //                 surveyStatus.completed = false;
  //                 surveyStatus.missingAnswers.push({
  //                   surveyPage: page,
  //                   surveySection: section,
  //                   surveyQuestion: question,
  //                   matrixItem: null,
  //                 });
  //               }
  //             }
  //           } else {
  //             for (const matrixItem of section.matrixItems) {
  //               for (const question of sectionQuestions) {
  //                 answer = answers.find(
  //                   (answer) =>
  //                     answer.surveyQuestionId === question.id &&
  //                     answer.matrixItemId === matrixItem.id
  //                 );

  //                 if (question.isRequired && !hasAnswer(answer)) {
  //                   surveyStatus.completed = false;
  //                   surveyStatus.missingAnswers.push({
  //                     surveyPage: page,
  //                     surveySection: section,
  //                     surveyQuestion: question,
  //                     matrixItem: matrixItem,
  //                   });
  //                 }
  //               }
  //             }
  //           }
  //         }
  //       }
  //     }
  //   }

  //   if (!surveyStatus.completed) {
  //     surveyStatus.colorCode = "#ff0000";
  //   }

  //   return surveyStatus;
  // };

  const getSection = (sectionId) => {
    if (!survey) return null;
    const section = survey.sections.find((section) => section.id === sectionId);
    return section;
  };

  const getPage = (pageId) => {
    if (!survey) return null;
    const page = survey.pages.find((page) => page.id === pageId);
    return page;
  };

  const validateQuestionMove = async (question) => {
    // is it controlled by another question?
    // does it control another question?

    if (question.controlledBy && question.controlledBy.questionId) {
      return false;
    }

    try {
      const controlsResults = await questionControls(question.id);

      // Safely check if controls exist
      if (
        controlsResults &&
        controlsResults.isSuccessful &&
        Array.isArray(controlsResults.data) &&
        controlsResults.data.length > 0
      ) {
        return false;
      }
    } catch (err) {
      console.error("Error validating question move:", err);
      // On error, allow the move to proceed (fail-safe)
      return true;
    }

    return true;
  };

  const questionControls = async (surveyQuestionId) => {
    try {
      const results = await executeAction({
        entityName: "SurveyQuestion",
        action: "controls",
        data: { surveyQuestionId },
      });

      // Ensure we always return a well-formed object
      if (!results) {
        return { isSuccessful: true, data: [] };
      }

      return results;
    } catch (err) {
      console.error("Error checking question controls:", err);
      return { isSuccessful: true, data: [] };
    }
  };

  const validateSectionMove = (section) => {
    if (section.controlledBy && section.controlledBy.questionId) {
      return false;
    }
    return true;
  };

  const validatePageMove = (page) => {
    if (page.controlledBy && page.controlledBy.questionId) {
      return false;
    }
    return true;
  };

  const getCallResult = async (surveyId, callId) => {
    const callResultData = await getFromServer({
      entityName: "CallResult",
      filter: { callId, surveyId },
    });

    if (Array.isArray(callResultData) && callResultData.length === 1) {
      return callResultData[0];
    }

    return callResultData;
  };

  const getAnswers = async (surveyId, callId) => {
    const callResult = await getCallResult(surveyId, callId);
    if (!callResult || callResult.length === 0) return [];

    const surveyAnswers = await getFromServer({
      entityName: "SurveyAnswer",
      filter: {
        callResultId: callResult.id,
      },
    });

    return surveyAnswers;
  };

  const getSurveyAnswerCount = async (surveyId) => {
    const results = await executeAction({
      entityName: "Survey",
      action: "surveyAnswerCount",
      data: { surveyId },
    });

    return results?.isSuccessful ? results : null;
  };

  const copySurvey = (
    fromSurvey,
    selectedPages,
    selectedSections,
    selectedQuestions,
    toSurvey,
  ) => {
    let newSurvey = structuredClone(toSurvey) || {
      pages: [],
      sections: [],
      questions: [],
    };

    if (!Array.isArray(newSurvey.pages)) newSurvey.pages = [];
    if (!Array.isArray(newSurvey.sections)) newSurvey.sections = [];
    if (!Array.isArray(newSurvey.questions)) newSurvey.questions = [];

    const cloneWithNewIds = (item) => {
      const cloned = structuredClone(item);
      cloned.id = "new-" + Math.random().toString(36).substr(2, 9);
      return cloned;
    };

    // Track ID mappings: originalId -> newId
    const pageIdMap = {};
    const sectionIdMap = {};

    // Copy selected pages with all their sections and questions
    for (const sourcePage of selectedPages) {
      const clonedPage = cloneWithNewIds(sourcePage);
      if (!clonedPage.name) clonedPage.name = "New Page";
      clonedPage.controlledBy = null;
      clonedPage.sections = [];

      for (const sourceSection of fromSurvey.sections) {
        if (sourceSection.surveyPageId !== sourcePage.id) continue;

        const clonedSection = cloneWithNewIds(sourceSection);
        if (!clonedSection.name) clonedSection.name = "New Section";
        clonedSection.controlledBy = null;
        clonedSection.surveyPageId = clonedPage.id;
        newSurvey.sections.push(clonedSection);

        for (const sourceQuestion of fromSurvey.questions) {
          if (sourceQuestion.surveySectionId !== sourceSection.id) continue;

          const clonedQuestion = cloneWithNewIds(sourceQuestion);
          if (!clonedQuestion.name) clonedQuestion.name = "New Question";
          clonedQuestion.controlledBy = null;
          clonedQuestion.surveySectionId = clonedSection.id;
          newSurvey.questions.push(clonedQuestion);
        }
      }

      newSurvey.pages.push(clonedPage);
    }

    // Copy selected sections with their questions
    for (const sourceSection of selectedSections) {
      // Always create a new page for each copied section
      const sourcePage = fromSurvey.pages.find(
        (p) => p.id === sourceSection.surveyPageId,
      );

      const pageToClone = sourcePage || {
        name: "New Page",
        pageNumber: newSurvey.pages.length,
      };
      const clonedPage = cloneWithNewIds(pageToClone);
      if (!clonedPage.name) clonedPage.name = "New Page";
      clonedPage.controlledBy = null;
      newSurvey.pages.push(clonedPage);

      // Clone and add the section
      const clonedSection = cloneWithNewIds(sourceSection);
      if (!clonedSection.name) clonedSection.name = "New Section";
      clonedSection.controlledBy = null;
      clonedSection.surveyPageId = clonedPage.id;
      newSurvey.sections.push(clonedSection);

      // Clone and add all questions for this section
      for (const sourceQuestion of fromSurvey.questions) {
        if (sourceQuestion.surveySectionId !== sourceSection.id) continue;

        const clonedQuestion = cloneWithNewIds(sourceQuestion);
        if (!clonedQuestion.name) clonedQuestion.name = "New Question";
        clonedQuestion.controlledBy = null;
        clonedQuestion.surveySectionId = clonedSection.id;
        newSurvey.questions.push(clonedQuestion);
      }
    }

    // Copy selected questions
    for (const sourceQuestion of selectedQuestions) {
      const sourceSect = fromSurvey.sections.find(
        (s) => s.id === sourceQuestion.surveySectionId,
      );

      if (!sourceSect) continue;

      // Create a new page with a new section for this question
      const sourcePage = fromSurvey.pages.find(
        (p) => p.id === sourceSect.surveyPageId,
      );

      const pageToClone = sourcePage || {
        name: "New Page",
        pageNumber: newSurvey.pages.length,
      };
      const clonedPage = cloneWithNewIds(pageToClone);
      if (!clonedPage.name) clonedPage.name = "New Page";
      clonedPage.controlledBy = null;
      newSurvey.pages.push(clonedPage);

      // Create section in new page
      const clonedSection = cloneWithNewIds(sourceSect);
      if (!clonedSection.name) clonedSection.name = "New Section";
      clonedSection.controlledBy = null;
      clonedSection.surveyPageId = clonedPage.id;
      newSurvey.sections.push(clonedSection);

      // Add question to flat array
      const clonedQuestion = cloneWithNewIds(sourceQuestion);
      if (!clonedQuestion.name) clonedQuestion.name = "New Question";
      clonedQuestion.controlledBy = null;
      clonedQuestion.surveySectionId = clonedSection.id;
      newSurvey.questions.push(clonedQuestion);
    }

    return newSurvey;
  };

  const saveCopy = async (
    fromSurvey,
    selectedPages,
    selectedSections,
    selectedQuestions,
    toSurvey,
    surveyName,
    copyOptions,
  ) => {
    let newSurvey = toSurvey
      ? structuredClone(toSurvey)
      : { pages: [], sections: [], questions: [] };

    if (!Array.isArray(newSurvey.pages)) newSurvey.pages = [];
    if (!Array.isArray(newSurvey.sections)) newSurvey.sections = [];
    if (!Array.isArray(newSurvey.questions)) newSurvey.questions = [];

    const shouldIncludeControlledBy =
      copyOptions?.includeControlledBy !== false;

    const cloneWithNewIds = (item) => {
      const cloned = structuredClone(item);
      // Preserve original source identity for backend controlledBy remapping.
      cloned.oldId = cloned.oldId || cloned.id || cloned._id || null;
      cloned.id = null;
      cloned.surveyId = null;
      cloned.surveyPageId = null;
      cloned.surveySectionId = null;
      if (!shouldIncludeControlledBy) {
        cloned.controlledBy = null;
      }
      return cloned;
    };

    // Copy selected pages with all their sections and questions (nested)
    let nextPageNumber = newSurvey.pages.length;
    for (const sourcePage of selectedPages) {
      const newPage = cloneWithNewIds(sourcePage);
      newPage.pageNumber = nextPageNumber++;
      newPage.sections = [];

      // Add sections for this page (nested)
      let nextSectionNumber = 0;
      for (const sourceSection of fromSurvey.sections) {
        if (sourceSection.surveyPageId !== sourcePage.id) continue;

        const newSection = cloneWithNewIds(sourceSection);
        newSection.sectionNumber = nextSectionNumber++;
        newSection.questions = [];

        // Add questions for this section (nested)
        let nextQuestionNumber = 0;
        for (const sourceQuestion of fromSurvey.questions) {
          if (sourceQuestion.surveySectionId !== sourceSection.id) continue;

          const newQuestion = cloneWithNewIds(sourceQuestion);
          newQuestion.questionNumber = nextQuestionNumber++;
          newSection.questions.push(newQuestion);
        }

        newPage.sections.push(newSection);
      }

      newSurvey.pages.push(newPage);
    }

    // Copy selected sections with their questions (nested in new pages)
    for (const sourceSection of selectedSections) {
      // Create a new page for each section being copied
      const sourcePage = fromSurvey.pages.find(
        (p) => p.id === sourceSection.surveyPageId,
      );

      const newPage = cloneWithNewIds(sourcePage || {});
      newPage.pageNumber = newSurvey.pages.length;
      newPage.sections = [];
      newSurvey.pages.push(newPage);

      // Add section to the new page
      const newSection = cloneWithNewIds(sourceSection);
      newSection.sectionNumber = 0;
      newSection.questions = [];

      // Add questions for this section
      let nextQuestionNumber = 0;
      for (const sourceQuestion of fromSurvey.questions) {
        if (sourceQuestion.surveySectionId !== sourceSection.id) continue;

        const newQuestion = cloneWithNewIds(sourceQuestion);
        newQuestion.questionNumber = nextQuestionNumber++;
        newSection.questions.push(newQuestion);
      }

      newPage.sections.push(newSection);
    }

    // Copy selected questions (nested in new pages->sections)
    for (const sourceQuestion of selectedQuestions) {
      const sourceSect = fromSurvey.sections.find(
        (s) => s.id === sourceQuestion.surveySectionId,
      );

      if (!sourceSect) continue;

      // Create a new page with a new section for this question
      const sourcePage = fromSurvey.pages.find(
        (p) => p.id === sourceSect.surveyPageId,
      );

      const newPage = cloneWithNewIds(sourcePage || {});
      newPage.pageNumber = newSurvey.pages.length;
      newPage.sections = [];
      newSurvey.pages.push(newPage);

      // Create section in new page
      const newSection = cloneWithNewIds(sourceSect);
      newSection.sectionNumber = 0;
      newSection.questions = [];
      newPage.sections.push(newSection);

      // Add question to section
      const newQuestion = cloneWithNewIds(sourceQuestion);
      newQuestion.questionNumber = 0;
      newSection.questions.push(newQuestion);
    }

    const customMessage = toSurvey
      ? "Items copied to current survey"
      : `Survey "${surveyName}" created`;

    await executeAction({
      entityName: "Survey",
      action: "copySurvey",
      data: {
        newSurvey,
        surveyName: toSurvey ? undefined : surveyName,
        targetSurveyId: toSurvey ? toSurvey.id : null,
        projectId: fromSurvey.projectId,
        surveyType: fromSurvey.surveyType,
        copyOptions,
      },
      displayMessage: true,
      customMessage,
    });
  };

  const getQuestionVariabilityData = async (section, question, call) => {
    if (!call.location) return "";

    let variabilityType = question.variability.variabilityType;
    let variabilityValue = await getVariabilityValue(section, question, call);

    let filter = {
      surveyQuestionId: question.id,
      variabilityType: variabilityType,
      variabilityValue: variabilityValue,
    };

    if (
      variabilityType === "sku" ||
      variabilityType === "category" ||
      variabilityType === "family"
    ) {
      filter.variabilityValue = { $in: variabilityValue };
    }

    if (variabilityType === "skuField" || variabilityType === "customField") {
      filter.variabilityType = question.variability.field;
    }

    const results = await getFromServer({
      entityName: "Variability",
      filter,
    });

    if (Array.isArray(results) && results.length > 0) {
      return results;
    }

    return null;
  };

  const getSectionVariabilityData = async (section, call) => {
    if (!call.location) return "";

    let variabilityType = section.variability.variabilityType;
    let variabilityValue = await getVariabilityValue(section, null, call);

    let filter = {
      surveySectionId: section.id,
      variabilityType: variabilityType,
      variabilityValue: variabilityValue,
    };

    if (
      variabilityType === "sku" ||
      variabilityType === "category" ||
      variabilityType === "family"
    ) {
      filter.variabilityValue = { $in: variabilityValue };
    }

    if (variabilityType === "skuField" || variabilityType === "customField") {
      filter.variabilityType = section.variability.field;
    }

    const results = await getFromServer({
      entityName: "Variability",
      filter,
    });

    if (Array.isArray(results) && results.length > 0) {
      return results;
    }

    return null;
  };

  const getVariabilityValue = async (section, question, call) => {
    let variabilityType = question
      ? question.variability.variabilityType
      : section.variability.variabilityType;
    let variabilityValue = null;

    let clientListings = null;
    if (
      variabilityType === "sku" ||
      variabilityType === "skuField" ||
      variabilityType === "category" ||
      variabilityType === "family"
    ) {
      clientListings = await getClientListings(
        call.client.id,
        call.location.id,
      );
    }

    switch (variabilityType) {
      case "retailer":
        variabilityValue = call.banner.retailerId;
        break;

      case "banner":
        variabilityValue = call.banner.id;
        break;

      case "locationId":
        variabilityValue = call.location.id;
        break;

      case "province":
        variabilityValue = call.location.address.province;
        break;

      case "category":
        variabilityValue = clientListings.map((listing) => listing.category);
        break;

      case "family":
        variabilityValue = clientListings.map((listing) => listing.family);
        break;

      case "sku":
        variabilityValue = clientListings.map((listing) => listing.skuId);
        break;

      case "skuField":
        break;

      case "customField":
        break;

      default:
        if (!Array.isArray(call.fields) || call.fields.length === 0) {
          break;
        }
        let field = call.fields.find((f) =>
          f.name === question
            ? question?.variability?.field
            : section.variability.field,
        );

        if (!field) break;

        variabilityValue = field.value;

        break;
    }

    return variabilityValue;
  };

  const getClientListings = async (clientId, locationId) => {
    const clientListings = await executeAction({
      entityName: "SKUListing",
      action: "getForLocation",
      data: {
        clientId,
        locationId,
      },
    });

    return clientListings;
  };

  // #region ORDER LOGIC
  const prepareOrderEntry = async (callId, surveyId) => {
    if (!callId || !surveyId) return;

    let results = await getFromServer({
      entityName: "CallResult",
      filter: {
        callId,
        surveyId,
      },
    });

    if (results.length > 0) {
      setCallResults(results[0]);
      await getOrder(surveyId, results[0].id, callId);
    }
  };

  const getOrder = async (surveyId, callResultsId, callId) => {
    setSurveyLoaded(false);

    let surveyResults = await getFromServer({
      entityName: "Survey",
      id: surveyId,
    });

    if (!surveyResults || surveyResults.length === 0) {
      setSurvey(null);
      setSkuList([]);
      setOrder(null);
      return;
    }

    setSurvey(surveyResults);

    const skuListData = await getSkuListData(callId);
    setSkuList(skuListData);

    const orderResult = await getOrderByCallResult(callResultsId);

    if (!orderResult || orderResult.length === 0) {
      setOrder(null);
      return;
    }

    const order = orderResult[0];

    // If order has items, enrich skuList with full SKU data for those items
    if (order.items && Array.isArray(order.items) && order.items.length > 0) {
      const skuIds = [...new Set(order.items.map((item) => item.skuId))];
      const enrichedList = [...skuListData];

      // Fetch full SKU data for each unique skuId in the order
      for (const skuId of skuIds) {
        const fullSku = await getFromServer({
          entityName: "SKU",
          id: skuId,
        });

        if (fullSku) {
          // Find and update the SKU in the enriched list, or add it if not present
          const existingIndex = enrichedList.findIndex((s) => s.id === skuId);
          if (existingIndex >= 0) {
            enrichedList[existingIndex] = {
              ...enrichedList[existingIndex],
              name: fullSku.name || enrichedList[existingIndex].name,
              category:
                fullSku.category || enrichedList[existingIndex].category,
            };
          } else {
            enrichedList.push(fullSku);
          }
        }
      }

      setSkuList(enrichedList);
    }

    setOrder(order);
  };

  const getSkuListData = async (callId) => {
    let skus = await skuListings(callId);

    const pricing = await skuPricing(callId);

    let withPricing = [];
    for (const sku of skus) {
      const price = findOne(pricing, { id: sku.id });
      withPricing.push({
        ...sku,
        price: price?.price ? price.price : sku.price,
      });
    }

    return withPricing;
  };

  const getSkuList = async (callId) => {
    const data = await getSkuListData(callId);
    setSkuList(data);
  };

  // #endregion

  return {
    setSurvey,
    survey,
    getById,
    getQuestionStatus,
    getSurveyStatus,
    getPage,
    getSection,
    validateQuestionMove,
    validateSectionMove,
    validatePageMove,
    getSurvey,
    getSkuStatus,
    skuStatus,
    getSkuMatrixStatus,
    getQuestionSectionStatus,
    getPageStatus,
    getCallResult,
    getAnswers,
    getSurveysForClient,
    getAllSurveys,
    surveys,
    copySurvey,
    saveCopy,
    getSurveyAnswerCount,
    getQuestionVariabilityData,
    getSectionVariabilityData,
    showQuestion,
    showSection,
    showPage,
    prepareOrderEntry,
    order,
    skuList,
    getSurveysForProject,
    showSurvey,
  };
};

export default useSurvey;
